/**
 * Actions Dashboard Page
 * Main page component for the SEO actions dashboard
 */

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IntegratedDashboard } from '../components/seo-dashboard/IntegratedDashboard';
import { apiCall } from '../services/api';
import { ArrowLeft, Trash2, Share2, Eye } from 'lucide-react';
import ShareModal from '../components/ShareModal';
import { useApp } from '../hooks/useApp';

export function ActionsDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { jobsRevision } = useApp();
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Get domain, timestamp, and jobId from URL params
  const params = new URLSearchParams(location.search);
  const domain = params.get('domain');
  const timestamp = params.get('timestamp');
  const jobId = params.get('jobId');

  useEffect(() => {
    fetchAnalyses();
  }, []);

  useEffect(() => {
    if (jobsRevision > 0) {
      fetchAnalyses({ silent: true });
    }
  }, [jobsRevision]);

  const fetchAnalyses = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const endpoint = jobId ? `/actions/analyses?jobId=${jobId}` : '/actions/analyses';
      const response = await apiCall(endpoint, {
        method: 'GET'
      });
      
      if (response.success) {
        console.log('Fetched analyses:', response.analyses);
        setAnalyses(response.analyses || []);
        
        // If we have a jobId but no analyses found, clear the jobId from URL.
        // Skip this on silent refreshes so a just-finished job isn't dropped
        // before its actions show up.
        if (!silent && jobId && (!response.analyses || response.analyses.length === 0)) {
          console.log('No analyses found for jobId:', jobId, '- clearing from URL');
          const url = new URL(window.location);
          url.searchParams.delete('jobId');
          window.history.replaceState({}, '', url);
        }
        
        // If we have analyses but no selection, select the most recent
        if (response.analyses.length > 0 && !domain && !timestamp) {
          const mostRecent = response.analyses[0];
          const url = new URL(window.location);
          // encode path-aware keys like "evnia.philips/au"
          url.searchParams.set('domain', mostRecent.domain);
          url.searchParams.set('timestamp', mostRecent.analysis_timestamp);
          window.history.replaceState({}, '', url);
          setSelectedAnalysis(mostRecent);
        } else if (domain && timestamp) {
          // Find the selected analysis (domain may be path-aware site key)
          const selected = response.analyses.find(
            a => a.domain === domain && a.analysis_timestamp.toString() === timestamp
          );
          setSelectedAnalysis(selected);
        } else if (jobId && response.analyses.length > 0) {
          // If we have jobId, select the first (should be only) analysis
          const analysis = response.analyses[0];
          setSelectedAnalysis(analysis);
        }
      }
    } catch (err) {
      console.error('Error fetching analyses:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleAnalysisChange = (analysisId) => {
    const analysis = analyses.find(a => a.id === analysisId);
    if (analysis) {
      navigate(
        `/dashboard/actions?domain=${encodeURIComponent(analysis.domain)}&timestamp=${analysis.analysis_timestamp}`
      );
      setSelectedAnalysis(analysis);
    }
  };
  
  const handleDeleteAnalysis = async () => {
    if (!selectedAnalysis) return;
    
    console.log('Deleting analysis:', selectedAnalysis);
    
    const confirmMessage = `Delete analysis for ${selectedAnalysis.domain}?\n\n` +
      `This will permanently delete:\n` +
      `• ${selectedAnalysis.actions_count} SEO action items\n` +
      `• All progress tracking data\n` +
      `• Analysis files and reports\n\n` +
      `This action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the action source
      const response = await apiCall(`/actions/analyses/${selectedAnalysis.id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        // Remove from local state
        const updatedAnalyses = analyses.filter(a => a.id !== selectedAnalysis.id);
        setAnalyses(updatedAnalyses);
        
        // Clear URL parameters to prevent reload issues
        const url = new URL(window.location);
        url.searchParams.delete('jobId');
        url.searchParams.delete('domain');
        url.searchParams.delete('timestamp');
        
        // Select another analysis or redirect
        if (updatedAnalyses.length > 0) {
          const nextAnalysis = updatedAnalyses[0];
          setSelectedAnalysis(nextAnalysis);
          url.searchParams.set('domain', nextAnalysis.domain);
          url.searchParams.set('timestamp', nextAnalysis.analysis_timestamp);
          window.history.replaceState({}, '', url);
        } else {
          // No more analyses, go to dashboard
          navigate('/dashboard');
          return;
        }
        
        alert('Analysis deleted successfully');
        
        // Force a re-fetch to ensure consistency
        setTimeout(() => fetchAnalyses(), 100);
      } else {
        alert('Failed to delete analysis: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting analysis:', error);
      alert('Error deleting analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading analyses...</p>
        </div>
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-border rounded-lg p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold text-foreground mb-4">No SEO Analyses Found</h2>
          <p className="text-muted-foreground mb-6">
            Run an SEO analysis first to see actionable recommendations here.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Analysis Selector */}
      {analyses.length > 0 && (
        <div className="bg-card border-b border-border px-6 py-3">
          <div className="flex items-center justify-between">
            
            {analyses.length > 1 && (
              <div className="flex items-center gap-4">
                <label
                  htmlFor="analysis-select"
                  className="text-sm font-medium text-card-foreground"
                >
                  Select Analysis:
                </label>
                <select
                  id="analysis-select"
                  value={selectedAnalysis?.id || ""}
                  onChange={(e) => handleAnalysisChange(e.target.value)}
                  className="border border-border rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {analyses.map((analysis) => (
                    <option key={analysis.id} value={analysis.id}>
                      {analysis.domain} -{" "}
                      {new Date(analysis.created_at).toLocaleDateString()}(
                      {analysis.actions_count} actions)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedAnalysis && (
              <>
                <button
                  onClick={() => navigate("/")}
                  className="flex items-left text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="text-sm">Back to All Jobs</span>
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-accent rounded transition-colors"
                    title="Share this analysis"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  <button
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (domain) params.set('domain', domain);
                      if (jobId) params.set('jobId', jobId);
                      const queryString = params.toString();
                      navigate(`/dashboard/shares${queryString ? `?${queryString}` : ''}`);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-card-foreground hover:bg-background rounded transition-colors"
                    title={domain || jobId ? `View shared links for ${domain || 'this job'}` : "View all shared links"}
                  >
                    <Eye className="w-4 h-4" />
                    View Shared Links
                  </button>
                </div>
                <button
                  onClick={handleDeleteAnalysis}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title="Delete this analysis and all associated data"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Analysis
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Dashboard */}
      {selectedAnalysis && (
        <IntegratedDashboard
          domain={selectedAnalysis.domain}
          timestamp={selectedAnalysis.analysis_timestamp}
          jobId={jobId}
          onSubscriptionInfoChange={setSubscriptionInfo}
        />
      )}

      {/* Share Modal */}
      {selectedAnalysis && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          jobId={jobId || selectedAnalysis.jobId || selectedAnalysis.job_id}
          title={`AI SEO Analysis for ${selectedAnalysis.domain}`}
          subscriptionInfo={subscriptionInfo}
        />
      )}
    </div>
  );
}